import React, { useEffect, useState } from "react";
import { ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const cls = "w-full rounded-xl border border-[#2A1B1D]/12 bg-white px-4 py-3 text-sm text-[#2A1B1D] outline-none transition-colors focus:border-[#D4AF37] placeholder:text-[#5C4A4D]/50";

export default function DeleteAccount() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", phone: "", email: "", login_method: "Google Sign-In", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    document.title = "Delete Your Munmun Ladies Beauty Parlour Account";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Request permanent deletion of your Munmun Ladies Beauty Parlour account and associated personal data.");
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!confirming) {
      if (!form.name.trim()) {
        toast.error("Please enter your name");
        return;
      }
      if (!form.phone.trim() && !form.email.trim()) {
        toast.error("Please provide the phone number or email linked to your account");
        return;
      }
      setConfirming(true);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/deletion-requests", form);
      setSubmitted(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed. Please try again.");
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const businessName = settings.name || "Munmun Ladies Beauty Parlour";

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain" data-testid="delete-account-page">
      <header className="border-b border-[#D4AF37]/25 bg-[#FDFBF7]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-2" data-testid="delete-logo">
            <span className="font-display text-2xl text-[#2A1B1D]">Munmun</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Beauty Parlour</span>
          </a>
          <a href="/" data-testid="delete-back-home" className="inline-flex items-center gap-2 text-sm text-[#5C4A4D] hover:text-[#8E3B46] transition-colors">
            <ArrowLeft size={16} /> Back to website
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Account &amp; Data</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3" data-testid="delete-page-title">
          Delete Your {businessName} Account
        </h1>
        <p className="mt-5 text-[#5C4A4D] leading-relaxed max-w-2xl">
          You can permanently delete your {businessName} account and all associated personal data at any time.
          This applies to accounts created or used through <strong>Google Sign-In</strong>, as well as any
          details provided while booking appointments through our website or mobile app.{" "}
          <strong>The deletion is automatic and immediate</strong> — as soon as you confirm, your account,
          profile and personal data are permanently removed, your session is signed out, and the account
          can no longer be used. No manual approval is required.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoCard title="What will be deleted">
            <ul className="space-y-2 text-sm text-[#5C4A4D] list-disc pl-5">
              <li>Your account profile (name, phone number, email address)</li>
              <li>Sign-in details linked to Google Sign-In</li>
              <li>Your appointment and booking history</li>
              <li>Saved preferences and any notes you added to bookings</li>
              <li>Reviews or feedback submitted under your details</li>
            </ul>
          </InfoCard>
          <InfoCard title="What may be retained">
            <ul className="space-y-2 text-sm text-[#5C4A4D] list-disc pl-5">
              <li>Anonymised records that cannot identify you (e.g. aggregate visit statistics)</li>
              <li>Records we are legally required to keep for taxation, accounting or regulatory purposes</li>
              <li>Fraud-prevention and security logs, retained for a limited period</li>
            </ul>
            <p className="mt-4 text-sm text-[#5C4A4D]">
              Any retained data is kept only for the minimum period required by law and is never used for
              marketing.
            </p>
          </InfoCard>
        </div>

        {/* Request form */}
        <div className="mt-14 rounded-3xl border border-[#D4AF37]/25 bg-white overflow-hidden">
          <div className="px-8 pt-8 pb-4 border-b border-[#D4AF37]/20 bg-[#F4EFEA]/50">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-[#8E3B46]" size={22} />
              <div>
                <h2 className="font-display text-2xl text-[#2A1B1D]">Delete My Account — Automatic</h2>
                <p className="text-xs text-[#5C4A4D] mt-1">Immediate and permanent. On the mobile app, you can also use Profile → Delete Account.</p>
              </div>
            </div>
          </div>

          {submitted ? (
            <div className="p-10 text-center" data-testid="delete-confirmation">
              <CheckCircle2 className="mx-auto text-[#3E664E]" size={52} />
              <h3 className="font-display text-3xl mt-4 text-[#2A1B1D]" data-testid="delete-success-message">
                Your account and applicable personal data have been permanently deleted.
              </h3>
              <p className="text-sm text-[#5C4A4D] mt-3 max-w-md mx-auto leading-relaxed">
                Reference: <span className="font-mono-x text-[#8E3B46] font-medium">{submitted.request_ref}</span>.
                {" "}Your sign-in session has been revoked and you have been signed out. This account can no
                longer be used.
              </p>
              <a href="/" className="mt-7 inline-block rounded-full bg-[#8E3B46] px-8 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">
                Return to website
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="p-8 space-y-4" data-testid="delete-request-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">Full name *</span>
                  <input value={form.name} onChange={set("name")} className={cls} placeholder="Your full name" data-testid="delete-name" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">How did you sign in?</span>
                  <select value={form.login_method} onChange={set("login_method")} className={cls} data-testid="delete-login-method">
                    <option>Google Sign-In</option>
                    <option>Phone / OTP</option>
                    <option>Email &amp; Password</option>
                    <option>Booked without an account</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">Phone number</span>
                  <input value={form.phone} onChange={set("phone")} className={cls} placeholder="+91 ..." data-testid="delete-phone" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">Email address</span>
                  <input type="email" value={form.email} onChange={set("email")} className={cls} placeholder="you@email.com" data-testid="delete-email" />
                </label>
              </div>
              <p className="text-xs text-[#5C4A4D]">Provide at least the phone number or the email address linked to your account so we can verify and locate it.</p>
              <label className="block">
                <span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">Reason (optional)</span>
                <textarea rows={3} value={form.reason} onChange={set("reason")} className={cls} placeholder="Anything you'd like us to know..." data-testid="delete-reason" />
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="delete-submit"
                className={`w-full sm:w-auto rounded-full px-10 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors disabled:opacity-60 ${
                  confirming ? "bg-[#2A1B1D] hover:bg-black" : "bg-[#8E3B46] hover:bg-[#722F38]"
                }`}
              >
                {submitting ? "Deleting..." : confirming ? "Confirm Permanent Deletion" : "Delete My Account"}
              </button>
              {confirming && (
                <div className="mt-3 rounded-xl bg-[#8E3B46]/5 border border-[#8E3B46]/20 p-4 text-xs text-[#8E3B46] flex items-start justify-between gap-3" data-testid="delete-confirm-warning">
                  <span>
                    This action is permanent and cannot be undone. Your account, profile, bookings and personal
                    data will be deleted immediately, and you will be signed out.
                  </span>
                  <button type="button" onClick={() => setConfirming(false)} className="shrink-0 underline" data-testid="delete-cancel">
                    Cancel
                  </button>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Contact fallback */}
        <div className="mt-10 rounded-2xl bg-[#F4EFEA] p-6 text-sm text-[#5C4A4D]">
          <p className="font-medium text-[#2A1B1D]">Prefer to contact us directly?</p>
          <p className="mt-2">
            You can also request deletion by contacting {businessName} at{" "}
            <span className="text-[#2A1B1D] font-medium">{settings.phone || "+91 82400 37030"}</span>
            {settings.email && <> or <span className="text-[#2A1B1D] font-medium">{settings.email}</span></>}.
          </p>
          <p className="mt-1">{settings.address || "Saratpally, Opposite Radha Govind Mandir, Bankra, Howrah, West Bengal 711403"}</p>
        </div>

        <div className="mt-14 pt-8 border-t border-[#2A1B1D]/10 text-xs text-[#5C4A4D]">
          © {new Date().getFullYear()} {businessName}. All rights reserved.
        </div>
      </main>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-3xl border border-[#2A1B1D]/8 bg-white p-7">
      <h3 className="font-display text-2xl text-[#2A1B1D] mb-3">{title}</h3>
      {children}
    </div>
  );
}
